import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Sound Sweeps — FTUE demo (3 essais).
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
const DEMO_STEPS = [
    {
        idx: 1,
        titleKey: 'sound_sweeps.demo.step1_title',
        hintKey: 'sound_sweeps.demo.step1_hint',
        direction: 'up',
        durationMs: 600,
    },
    {
        idx: 2,
        titleKey: 'sound_sweeps.demo.step2_title',
        hintKey: 'sound_sweeps.demo.step2_hint',
        direction: 'down',
        durationMs: 400,
    },
    {
        idx: 3,
        titleKey: 'sound_sweeps.demo.step3_title',
        hintKey: 'sound_sweeps.demo.step3_hint',
        direction: 'up',
        durationMs: 250,
    },
];
function playDemoSweep(direction, durationMs) {
    if (typeof window === 'undefined')
        return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const Ctor = (window.AudioContext ?? window.webkitAudioContext);
    if (!Ctor)
        return;
    const ctx = new Ctor();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    const fLow = 700;
    const fHigh = 1400;
    const start = direction === 'up' ? fLow : fHigh;
    const end = direction === 'up' ? fHigh : fLow;
    osc.frequency.setValueAtTime(start, ctx.currentTime);
    osc.frequency.linearRampToValueAtTime(end, ctx.currentTime + durationMs / 1000);
    gain.gain.setValueAtTime(0, ctx.currentTime);
    gain.gain.linearRampToValueAtTime(0.18, ctx.currentTime + 0.01);
    gain.gain.linearRampToValueAtTime(0, ctx.currentTime + durationMs / 1000);
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + durationMs / 1000);
    setTimeout(() => {
        try {
            void ctx.close();
        }
        catch {
            /* ignored */
        }
    }, durationMs + 100);
}
export function Demo({ level: _level, onComplete, onSkip }) {
    const { t } = useTranslation(['games', 'common']);
    const [stepIdx, setStepIdx] = useState(0);
    const [phase, setPhase] = useState('ready');
    const [pick, setPick] = useState(null);
    const playedOnceRef = useRef(false);
    const step = DEMO_STEPS[stepIdx];
    const isLast = stepIdx === DEMO_STEPS.length - 1;
    const correct = pick === step.direction;
    useEffect(() => {
        playedOnceRef.current = false;
        setPick(null);
        setPhase('ready');
    }, [stepIdx]);
    const handlePlay = () => {
        if (phase !== 'ready' && phase !== 'awaiting' && phase !== 'playing')
            return;
        playedOnceRef.current = true;
        setPhase('playing');
        playDemoSweep(step.direction, step.durationMs);
        setTimeout(() => setPhase('awaiting'), step.durationMs + 200);
    };
    const handlePick = (d) => {
        if (phase !== 'awaiting')
            return;
        setPick(d);
        setPhase('feedback');
    };
    const handleNext = () => {
        if (isLast) {
            onComplete();
            return;
        }
        setStepIdx((i) => i + 1);
    };
    return (_jsxs("div", { className: "flex flex-1 flex-col items-center gap-s4 py-s4 text-center", children: [_jsx("p", { className: "font-mono text-[9.5px] uppercase tracking-[0.30em] text-cyan-text-faint", children: t('common:demo.eyebrow_with_master', { master: t('sound_sweeps.demo.master') }) }), _jsx("h2", { className: "font-display text-[22px] font-medium leading-7 tracking-cy-display text-cyan-text", children: t(step.titleKey) }), _jsx("p", { className: "max-w-[320px] text-[13.5px] text-cyan-text-secondary", children: t(step.hintKey) }), _jsxs("div", { className: "flex h-[160px] w-full max-w-[280px] items-center justify-center", style: { background: '#1a1a1a' }, children: [phase === 'playing' && (_jsx("span", { className: "font-display text-[16px] text-cyan-surface animate-pulse", children: "\u266A \u00E9coute \u266A" })), phase === 'ready' && !playedOnceRef.current && (_jsx("span", { className: "font-display text-[14px] text-cyan-surface", children: t('sound_sweeps.demo.ready_phrase') })), phase === 'awaiting' && (_jsx("span", { className: "font-display text-[18px] text-cyan-surface", children: t('sound_sweeps.demo.awaiting_question') })), phase === 'feedback' && (_jsx("span", { className: [
                            'font-display text-[20px] font-medium',
                            correct ? 'text-cyan-core' : 'text-cyan-media',
                        ].join(' '), children: correct
                            ? '✓'
                            : `✗ — ${t('sound_sweeps.demo.feedback_incorrect', {
                                direction: t(`sound_sweeps.demo.direction_${step.direction}`),
                            })}` }))] }), _jsxs("div", { className: "flex flex-col items-stretch gap-s3 self-stretch", children: [(phase === 'ready' || phase === 'awaiting') && (_jsx("button", { type: "button", onClick: handlePlay, className: "inline-flex h-12 w-full items-center justify-center bg-cyan-core font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-surface transition active:scale-[0.985] hover:bg-cyan-network", children: t('sound_sweeps.demo.play_sound') })), phase === 'awaiting' && (_jsxs("div", { className: "flex justify-center gap-s3", children: [_jsx("button", { type: "button", onClick: () => handlePick('up'), "aria-label": t('sound_sweeps.demo.answer_up_aria'), className: "flex h-[60px] w-[60px] items-center justify-center rounded-full border border-cyan-border bg-cyan-surface text-[28px] text-cyan-text transition active:scale-95 hover:bg-cyan-bg", children: "\u2191" }), _jsx("button", { type: "button", onClick: () => handlePick('down'), "aria-label": t('sound_sweeps.demo.answer_down_aria'), className: "flex h-[60px] w-[60px] items-center justify-center rounded-full border border-cyan-border bg-cyan-surface text-[28px] text-cyan-text transition active:scale-95 hover:bg-cyan-bg", children: "\u2193" })] })), phase === 'feedback' && (_jsx("button", { type: "button", onClick: handleNext, className: "inline-flex h-12 w-full items-center justify-center bg-cyan-core font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-surface transition active:scale-[0.985] hover:bg-cyan-network", children: isLast ? t('common:actions.start_session') : t('common:actions.next_trial') })), _jsx("button", { type: "button", onClick: onSkip, className: "text-[12px] text-cyan-text-secondary underline-offset-4 hover:underline", children: t('common:actions.skip_demo') })] })] }));
}
//# sourceMappingURL=demo.js.map